import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AuditConfiguration = () => {
  const [auditSettings, setAuditSettings] = useState({
    enableAuditLogging: true,
    logLevel: 'detailed',
    retentionPeriodDays: 2555, // 7 years
    maxLogSize: 1000, // MB
    autoArchive: true,
    archiveAfterDays: 365,
    realTimeMonitoring: true,
    alertThreshold: 1000 // events per hour
  });

  const [eventTypes, setEventTypes] = useState({
    userActions: true,
    documentChanges: true,
    systemChanges: true,
    loginAttempts: true,
    failedLogins: true,
    permissionChanges: true,
    configurationChanges: true,
    dataExports: true,
    apiAccess: true,
    fileUploads: true,
    fileDownloads: true,
    searchQueries: false
  });

  const [exportSettings, setExportSettings] = useState({
    enableScheduledExports: false,
    exportFrequency: 'monthly',
    exportFormat: 'csv',
    includeUserData: true,
    includeSystemData: true,
    encryptExports: true,
    emailReports: true,
    reportRecipients: 'admin@company.com'
  });

  const [complianceSettings, setComplianceSettings] = useState({
    gdprCompliance: true,
    hipaaCompliance: false,
    soxCompliance: true,
    customCompliance: false,
    dataAnonymization: true,
    rightToErasure: true,
    consentTracking: true
  });

  const [hasChanges, setHasChanges] = useState(false);

  const logLevelOptions = [
    { value: 'minimal', label: 'Minimal (Critical events only)' },
    { value: 'standard', label: 'Standard (Important events)' },
    { value: 'detailed', label: 'Detailed (All events)' },
    { value: 'verbose', label: 'Verbose (Debug level)' }
  ];

  const retentionOptions = [
    { value: 365, label: '1 Year' },
    { value: 1095, label: '3 Years' },
    { value: 1825, label: '5 Years' },
    { value: 2555, label: '7 Years' },
    { value: 3650, label: '10 Years' }
  ];

  const exportFrequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const exportFormatOptions = [
    { value: 'csv', label: 'CSV' },
    { value: 'json', label: 'JSON' },
    { value: 'xml', label: 'XML' },
    { value: 'pdf', label: 'PDF Report' }
  ];

  const handleAuditChange = (field, value) => {
    setAuditSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleEventTypeChange = (field, value) => {
    setEventTypes(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleExportChange = (field, value) => {
    setExportSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleComplianceChange = (field, value) => {
    setComplianceSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const calculateStorageImpact = () => {
    const eventsPerDay = auditSettings?.logLevel === 'verbose' ? 50000 :
                        auditSettings?.logLevel === 'detailed' ? 25000 :
                        auditSettings?.logLevel === 'standard' ? 10000 : 5000;
    
    const avgEventSize = 0.5; // KB
    const dailyStorage = (eventsPerDay * avgEventSize) / 1024; // MB
    const totalStorage = dailyStorage * auditSettings?.retentionPeriodDays;
    
    return {
      daily: dailyStorage?.toFixed(1),
      total: totalStorage?.toFixed(1),
      events: eventsPerDay?.toLocaleString()
    };
  };

  const storageImpact = calculateStorageImpact();

  const handleSave = () => {
    console.log('Saving audit configuration:', {
      auditSettings,
      eventTypes,
      exportSettings,
      complianceSettings
    });
    setHasChanges(false);
  };

  const handleReset = () => {
    setAuditSettings({
      enableAuditLogging: true,
      logLevel: 'detailed',
      retentionPeriodDays: 2555,
      maxLogSize: 1000,
      autoArchive: true,
      archiveAfterDays: 365,
      realTimeMonitoring: true,
      alertThreshold: 1000
    });
    setEventTypes({
      userActions: true,
      documentChanges: true,
      systemChanges: true,
      loginAttempts: true,
      failedLogins: true,
      permissionChanges: true,
      configurationChanges: true,
      dataExports: true,
      apiAccess: true,
      fileUploads: true,
      fileDownloads: true,
      searchQueries: false
    });
    setExportSettings({
      enableScheduledExports: false,
      exportFrequency: 'monthly',
      exportFormat: 'csv',
      includeUserData: true,
      includeSystemData: true,
      encryptExports: true,
      emailReports: true,
      reportRecipients: 'admin@company.com'
    });
    setComplianceSettings({
      gdprCompliance: true,
      hipaaCompliance: false,
      soxCompliance: true,
      customCompliance: false,
      dataAnonymization: true,
      rightToErasure: true,
      consentTracking: true
    });
    setHasChanges(false);
  };

  return (
    <div className="space-y-8">
      {/* Audit Logging Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Audit Logging Configuration</h3>
            <p className="text-sm text-muted-foreground">Configure system audit trail and logging settings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Enable Audit Logging"
              description="Track and log all system activities"
              checked={auditSettings?.enableAuditLogging}
              onChange={(e) => handleAuditChange('enableAuditLogging', e?.target?.checked)}
            />

            <Select
              label="Logging Level"
              description="Detail level of audit logs"
              options={logLevelOptions}
              value={auditSettings?.logLevel}
              onChange={(value) => handleAuditChange('logLevel', value)}
              disabled={!auditSettings?.enableAuditLogging}
            />

            <Select
              label="Retention Period"
              description="How long to keep audit logs"
              options={retentionOptions}
              value={auditSettings?.retentionPeriodDays}
              onChange={(value) => handleAuditChange('retentionPeriodDays', value)}
              disabled={!auditSettings?.enableAuditLogging}
            />
          </div>

          <div className="space-y-4">
            <Input
              label="Max Log File Size (MB)"
              type="number"
              value={auditSettings?.maxLogSize}
              onChange={(e) => handleAuditChange('maxLogSize', parseInt(e?.target?.value))}
              min={100}
              max={10000}
              description="Maximum size before log rotation"
              disabled={!auditSettings?.enableAuditLogging}
            />

            <Checkbox
              label="Auto-archive old logs"
              description="Automatically compress and archive old log files"
              checked={auditSettings?.autoArchive}
              onChange={(e) => handleAuditChange('autoArchive', e?.target?.checked)}
              disabled={!auditSettings?.enableAuditLogging}
            />

            {auditSettings?.autoArchive && (
              <Input
                label="Archive after (days)"
                type="number"
                value={auditSettings?.archiveAfterDays}
                onChange={(e) => handleAuditChange('archiveAfterDays', parseInt(e?.target?.value))}
                min={30}
                max={365}
                className="ml-6"
                disabled={!auditSettings?.enableAuditLogging}
              />
            )}
          </div>
        </div>

        {/* Storage Impact */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Storage Impact Estimate</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Daily Events:</span>
              <p className="font-medium text-foreground">{storageImpact?.events}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Daily Storage:</span>
              <p className="font-medium text-foreground">{storageImpact?.daily} MB</p>
            </div>
            <div>
              <span className="text-muted-foreground">Total Storage:</span>
              <p className="font-medium text-foreground">{storageImpact?.total} MB</p>
            </div>
          </div>
        </div>
      </div>
      {/* Event Types Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Activity" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Event Types to Log</h3>
            <p className="text-sm text-muted-foreground">Select which types of events to include in audit logs</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">User Activities</h4>
            <div className="space-y-2">
              <Checkbox
                label="User actions"
                description="Document views, edits, downloads"
                checked={eventTypes?.userActions}
                onChange={(e) => handleEventTypeChange('userActions', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="Login attempts"
                description="Successful and failed logins"
                checked={eventTypes?.loginAttempts}
                onChange={(e) => handleEventTypeChange('loginAttempts', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="Failed logins only"
                description="Security-focused login tracking"
                checked={eventTypes?.failedLogins}
                onChange={(e) => handleEventTypeChange('failedLogins', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="Search queries"
                description="User search activities"
                checked={eventTypes?.searchQueries}
                onChange={(e) => handleEventTypeChange('searchQueries', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Document Activities</h4>
            <div className="space-y-2">
              <Checkbox
                label="Document changes"
                description="Creates, updates, deletes"
                checked={eventTypes?.documentChanges}
                onChange={(e) => handleEventTypeChange('documentChanges', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="File uploads"
                description="New file uploads to system"
                checked={eventTypes?.fileUploads}
                onChange={(e) => handleEventTypeChange('fileUploads', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="File downloads"
                description="Document download activities"
                checked={eventTypes?.fileDownloads}
                onChange={(e) => handleEventTypeChange('fileDownloads', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="Data exports"
                description="Bulk data export operations"
                checked={eventTypes?.dataExports}
                onChange={(e) => handleEventTypeChange('dataExports', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">System Activities</h4>
            <div className="space-y-2">
              <Checkbox
                label="System changes"
                description="Configuration modifications"
                checked={eventTypes?.systemChanges}
                onChange={(e) => handleEventTypeChange('systemChanges', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="Permission changes"
                description="User role and access modifications"
                checked={eventTypes?.permissionChanges}
                onChange={(e) => handleEventTypeChange('permissionChanges', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="Configuration changes"
                description="System setting modifications"
                checked={eventTypes?.configurationChanges}
                onChange={(e) => handleEventTypeChange('configurationChanges', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
              <Checkbox
                label="API access"
                description="External API calls and integrations"
                checked={eventTypes?.apiAccess}
                onChange={(e) => handleEventTypeChange('apiAccess', e?.target?.checked)}
                disabled={!auditSettings?.enableAuditLogging}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Real-time Monitoring Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Real-time Monitoring</h3>
            <p className="text-sm text-muted-foreground">Configure alerts and real-time audit monitoring</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Enable real-time monitoring"
              description="Monitor audit events as they occur"
              checked={auditSettings?.realTimeMonitoring}
              onChange={(e) => handleAuditChange('realTimeMonitoring', e?.target?.checked)}
              disabled={!auditSettings?.enableAuditLogging}
            />

            <Input
              label="Alert Threshold (events/hour)"
              type="number"
              value={auditSettings?.alertThreshold}
              onChange={(e) => handleAuditChange('alertThreshold', parseInt(e?.target?.value))}
              min={100}
              max={10000}
              description="Alert when event rate exceeds this threshold"
              disabled={!auditSettings?.enableAuditLogging || !auditSettings?.realTimeMonitoring}
            />
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-accent/10 border border-accent/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Info" size={16} className="text-accent" />
                <span className="text-sm font-medium text-accent">Current Status</span>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Events logged today: 12,847</p>
                <p>Current rate: 534 events/hour</p>
                <p>Status: Normal</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Export & Reporting Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Download" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Export & Reporting</h3>
            <p className="text-sm text-muted-foreground">Configure automated audit report generation</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Enable scheduled exports"
              description="Automatically generate and export audit reports"
              checked={exportSettings?.enableScheduledExports}
              onChange={(e) => handleExportChange('enableScheduledExports', e?.target?.checked)}
              disabled={!auditSettings?.enableAuditLogging}
            />

            <Select
              label="Export Frequency"
              options={exportFrequencyOptions}
              value={exportSettings?.exportFrequency}
              onChange={(value) => handleExportChange('exportFrequency', value)}
              disabled={!auditSettings?.enableAuditLogging || !exportSettings?.enableScheduledExports}
            />

            <Select
              label="Export Format"
              options={exportFormatOptions}
              value={exportSettings?.exportFormat}
              onChange={(value) => handleExportChange('exportFormat', value)}
              disabled={!auditSettings?.enableAuditLogging || !exportSettings?.enableScheduledExports}
            />
          </div>

          <div className="space-y-4">
            <Checkbox
              label="Include user data"
              description="Include user information in reports"
              checked={exportSettings?.includeUserData}
              onChange={(e) => handleExportChange('includeUserData', e?.target?.checked)}
              disabled={!auditSettings?.enableAuditLogging || !exportSettings?.enableScheduledExports}
            />

            <Checkbox
              label="Include system data"
              description="Include system events in reports"
              checked={exportSettings?.includeSystemData}
              onChange={(e) => handleExportChange('includeSystemData', e?.target?.checked)}
              disabled={!auditSettings?.enableAuditLogging || !exportSettings?.enableScheduledExports}
            />

            <Checkbox
              label="Encrypt exports"
              description="Password-protect exported files"
              checked={exportSettings?.encryptExports}
              onChange={(e) => handleExportChange('encryptExports', e?.target?.checked)}
              disabled={!auditSettings?.enableAuditLogging || !exportSettings?.enableScheduledExports}
            />

            <Checkbox
              label="Email reports"
              description="Send reports via email"
              checked={exportSettings?.emailReports}
              onChange={(e) => handleExportChange('emailReports', e?.target?.checked)}
              disabled={!auditSettings?.enableAuditLogging || !exportSettings?.enableScheduledExports}
            />

            {exportSettings?.emailReports && (
              <Input
                label="Report Recipients"
                value={exportSettings?.reportRecipients}
                onChange={(e) => handleExportChange('reportRecipients', e?.target?.value)}
                description="Comma-separated email addresses"
                disabled={!auditSettings?.enableAuditLogging || !exportSettings?.enableScheduledExports}
              />
            )}
          </div>
        </div>
      </div>
      {/* Compliance Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-error" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Compliance Settings</h3>
            <p className="text-sm text-muted-foreground">Configure compliance-specific audit requirements</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Regulatory Compliance</h4>
            <div className="space-y-3">
              <Checkbox
                label="GDPR Compliance"
                description="European data protection regulation"
                checked={complianceSettings?.gdprCompliance}
                onChange={(e) => handleComplianceChange('gdprCompliance', e?.target?.checked)}
              />
              <Checkbox
                label="HIPAA Compliance"
                description="Healthcare information privacy"
                checked={complianceSettings?.hipaaCompliance}
                onChange={(e) => handleComplianceChange('hipaaCompliance', e?.target?.checked)}
              />
              <Checkbox
                label="SOX Compliance"
                description="Sarbanes-Oxley financial reporting"
                checked={complianceSettings?.soxCompliance}
                onChange={(e) => handleComplianceChange('soxCompliance', e?.target?.checked)}
              />
              <Checkbox
                label="Custom compliance framework"
                description="Organization-specific requirements"
                checked={complianceSettings?.customCompliance}
                onChange={(e) => handleComplianceChange('customCompliance', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Privacy Features</h4>
            <div className="space-y-3">
              <Checkbox
                label="Data anonymization"
                description="Anonymize personal data in logs"
                checked={complianceSettings?.dataAnonymization}
                onChange={(e) => handleComplianceChange('dataAnonymization', e?.target?.checked)}
              />
              <Checkbox
                label="Right to erasure"
                description="Support data deletion requests"
                checked={complianceSettings?.rightToErasure}
                onChange={(e) => handleComplianceChange('rightToErasure', e?.target?.checked)}
              />
              <Checkbox
                label="Consent tracking"
                description="Track user consent for data processing"
                checked={complianceSettings?.consentTracking}
                onChange={(e) => handleComplianceChange('consentTracking', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex items-center justify-between p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <span className="text-sm font-medium text-warning">You have unsaved changes</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset}>
              Reset Changes
            </Button>
            <Button variant="default" onClick={handleSave}>
              Save Audit Configuration
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditConfiguration;